import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../auth/AuthProvider";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from "recharts";
import Sidebar from "../components/Sidebar";
import { getInvoicePDFBase64 } from "../components/InvoicePDF";
import { useCompany } from "../hooks/useCompany";

import { BULK_BATCH_SIZE, BULK_MAX_BATCHES } from "../constants";
import { errorDetail } from "../utils/apiError";

const API_URL = process.env.REACT_APP_API_URL;
const TRIAL_INVOICE_LIMIT = 3;
const PAGE_SIZE_OPTIONS = [25, 50, 100];

const PAYMENT_METHODS = [
  { value: "check",        label: "Check" },
  { value: "cash",         label: "Cash" },
  { value: "zelle",        label: "Zelle" },
  { value: "venmo",        label: "Venmo" },
  { value: "bank_transfer",label: "Bank Transfer" },
  { value: "other",        label: "Other" },
];

const METHOD_BADGE = {
  stripe:        { label: "Stripe",        color: "bg-purple-100 text-purple-700" },
  check:         { label: "Check",         color: "bg-blue-100 text-blue-700" },
  cash:          { label: "Cash",          color: "bg-green-100 text-green-700" },
  zelle:         { label: "Zelle",         color: "bg-indigo-100 text-indigo-700" },
  venmo:         { label: "Venmo",         color: "bg-sky-100 text-sky-700" },
  bank_transfer: { label: "Bank Transfer", color: "bg-teal-100 text-teal-700" },
  other:         { label: "Other",         color: "bg-gray-100 text-gray-600" },
};

const fmt = (n) => `$${(parseFloat(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const isOverdue = (inv) =>
  !inv.paid && inv.due_date && new Date(inv.due_date) < new Date(new Date().toISOString().split("T")[0]);

const InvoiceList = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isDemo, activeCompany, subscriptionData } = useCompany();
  const isAdmin = subscriptionData?.is_admin === true;

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [markingPaid, setMarkingPaid] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [generatingLink, setGeneratingLink] = useState(null);
  const [filter, setFilter] = useState("unpaid"); // all | unpaid | paid | overdue
  const [sort, setSort] = useState({ key: "invoice_number", dir: "desc" });
  const [tooltip, setTooltip] = useState(null); // { text, x, y }

  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientDropdownOpen, setClientDropdownOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkSending, setBulkSending] = useState(false);
  const clientSearchRef = useRef(null);
  const bulkSendingRef = useRef(false);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const showTooltip = (e, text) => {
    const r = e.currentTarget.getBoundingClientRect();
    setTooltip({ text, x: r.left + r.width / 2, y: r.top });
  };
  const hideTooltip = () => setTooltip(null);

  const token = auth.user?.access_token;

  const fetchInvoices = useCallback(async () => {
    if (!auth.isAuthenticated) return;
    try {
      const res = await fetch(`${API_URL}/invoices/all`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) { setInvoices([]); return; }
      if (!res.ok) throw new Error(`Failed to fetch invoices: ${res.status}`);
      const data = await res.json();
      setInvoices(
        activeCompany
          ? data.filter(inv => !inv.company_id || inv.company_id === activeCompany.company_id)
          : data
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [auth.isAuthenticated, token, activeCompany]);

  // Initial fetch
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // Refetch when the owner switches back to this tab (e.g. after a client pays)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && !bulkSendingRef.current) fetchInvoices();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchInvoices]);

  // Poll every 30 s while the tab is visible — skipped during bulk send to avoid Lambda throttle
  useEffect(() => {
    const id = setInterval(() => {
      if (document.visibilityState === "visible" && !bulkSendingRef.current) fetchInvoices();
    }, 30000);
    return () => clearInterval(id);
  }, [fetchInvoices]);

  useEffect(() => {
    if (!auth.isAuthenticated) return;
    fetch(`${API_URL}/clients/all`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setClients)
      .catch(() => {});
  }, [auth.isAuthenticated, token]);

  useEffect(() => {
    const handler = (e) => {
      if (clientSearchRef.current && !clientSearchRef.current.contains(e.target))
        setClientDropdownOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Block browser refresh / tab close during bulk send
  useEffect(() => {
    if (!bulkSending) return;
    const handler = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [bulkSending]);

  // Block browser back/forward navigation during bulk send
  useEffect(() => {
    if (!bulkSending) return;
    window.history.pushState(null, "", window.location.href);
    const handlePopState = () => {
      window.history.pushState(null, "", window.location.href);
      Swal.fire({
        title: "Bulk Send in Progress",
        text: "Please wait until all emails have been sent before navigating away.",
        icon: "warning",
        confirmButtonText: "OK",
        confirmButtonColor: "#22c55e",
      });
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [bulkSending]);

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const paid       = invoices.filter(i => i.paid);
    const unpaid     = invoices.filter(i => !i.paid);
    const overdue    = invoices.filter(isOverdue);
    const paidAmt    = paid.reduce((s, i) => s + (parseFloat(i.total_amount) || 0), 0);
    const unpaidAmt  = unpaid.reduce((s, i) => s + (parseFloat(i.total_amount) || 0), 0);
    return { paid, unpaid, overdue, paidAmt, unpaidAmt };
  }, [invoices]);

  // Bar chart — last 6 months of invoice totals
  const monthlyData = useMemo(() => {
    const now = new Date();
    const months = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
      return {
        name: d.toLocaleString("default", { month: "short" }),
        year: d.getFullYear(),
        month: d.getMonth(),
        paid: 0,
        unpaid: 0,
      };
    });
    invoices.forEach(inv => {
      const d = new Date(inv.date);
      const slot = months.find(m => m.year === d.getFullYear() && m.month === d.getMonth());
      if (!slot) return;
      if (inv.paid) slot.paid   += parseFloat(inv.total_amount) || 0;
      else          slot.unpaid += parseFloat(inv.total_amount) || 0;
    });
    return months.map(m => ({ name: m.name, Paid: +m.paid.toFixed(2), Unpaid: +m.unpaid.toFixed(2) }));
  }, [invoices]);

  // ── Sorting & filtering ──────────────────────────────────────────────────
  const clientSuggestions = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    const pool = q
      ? clients.filter(c => c.name.toLowerCase().includes(q))
      : [...clients].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
    return pool.slice(0, 5);
  }, [clients, clientSearch]);

  const clientBase = useMemo(() =>
    selectedClient ? invoices.filter(i => i.client_name === selectedClient.name) : invoices,
  [invoices, selectedClient]);

  const tabCounts = useMemo(() => ({
    all:     clientBase.length,
    unpaid:  clientBase.filter(i => !i.paid).length,
    paid:    clientBase.filter(i => i.paid).length,
    overdue: clientBase.filter(isOverdue).length,
  }), [clientBase]);

  const filtered = useMemo(() => {
    if (filter === "paid")    return clientBase.filter(i => i.paid);
    if (filter === "unpaid")  return clientBase.filter(i => !i.paid);
    if (filter === "overdue") return clientBase.filter(isOverdue);
    return clientBase;
  }, [clientBase, filter]);

  const sorted = useMemo(() => {
    const { key, dir } = sort;
    return [...filtered].sort((a, b) => {
      const av = a[key] ?? "", bv = b[key] ?? "";
      if (key === "total_amount" || key === "invoice_number")
        return dir === "asc" ? Number(av) - Number(bv) : Number(bv) - Number(av);
      if (key === "paid")
        return dir === "asc" ? (a.paid ? 1 : -1) : (b.paid ? 1 : -1);
      return dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((safePage - 1) * pageSize, safePage * pageSize);

  const pageNumbers = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, safePage - delta);
    const right = Math.min(totalPages, safePage + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push("…"); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push("…"); pages.push(totalPages); }
    return pages;
  };

  const toggleSort = (key) => {
    setSort(prev => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));
    setCurrentPage(1);
  };

  // Header checkbox controls the current page only; selections accumulate across pages
  const allPageSelected = paginated.length > 0 && paginated.every(inv => selectedIds.has(inv.invoice_id));
  const somePageSelected = paginated.some(inv => selectedIds.has(inv.invoice_id));

  // Disable bulk send when selected invoices span more than one client email
  const selectedWithEmail = sorted.filter(inv => selectedIds.has(inv.invoice_id) && inv.client_email);
  const clientEmailMap = new Map(selectedWithEmail.map(inv => [inv.client_email, inv.client_name]));
  const hasEmailConflict = clientEmailMap.size > 1;
  const conflictingClients = hasEmailConflict
    ? [...clientEmailMap.entries()].map(([email, name]) => ({ name, email }))
    : [];

  const toggleSelect = (id) =>
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (allPageSelected) {
        paginated.forEach(inv => next.delete(inv.invoice_id));
      } else {
        paginated.forEach(inv => next.add(inv.invoice_id));
      }
      return next;
    });
  };

  const handleBulkSend = async () => {
    const toSend = sorted.filter(inv => selectedIds.has(inv.invoice_id));
    const withEmail = toSend.filter(inv => inv.client_email);
    const skippedCount = toSend.length - withEmail.length;

    if (withEmail.length === 0) {
      Swal.fire({ icon: "warning", title: "No emails to send", text: "None of the selected invoices have a client email on file." });
      return;
    }

    const maxTotal = BULK_BATCH_SIZE * BULK_MAX_BATCHES;
    const capped = withEmail.slice(0, maxTotal);
    const overLimit = withEmail.length > maxTotal;

    // Split into batches of BULK_BATCH_SIZE
    const batches = [];
    for (let i = 0; i < capped.length; i += BULK_BATCH_SIZE) {
      batches.push(capped.slice(i, i + BULK_BATCH_SIZE));
    }

    bulkSendingRef.current = true;
    setBulkSending(true);
    let totalSent = 0;
    let successfulBatches = 0;
    let failedCount = 0;
    let failureReason = null;

    for (let batchIdx = 0; batchIdx < batches.length; batchIdx++) {
      if (batchIdx > 0) await new Promise(r => setTimeout(r, 1000));
      const batch = batches[batchIdx];
      const recipientEmail = batch[0].client_email;
      try {
        const invoicesPayload = await Promise.all(batch.map(async (inv) => {
          const doc = {
            ...inv,
            business_logo_url: activeCompany?.logo_url || "",
            payment_url: (!inv.paid && activeCompany?.stripe_connect_account_id)
              ? `${window.location.origin}/pay/${inv.invoice_id}`
              : null,
          };
          const pdf_base64 = await getInvoicePDFBase64(doc);
          return { invoice_id: inv.invoice_id, pdf_base64, payment_url: doc.payment_url };
        }));

        const body = JSON.stringify({ recipient_email: recipientEmail, invoices: invoicesPayload });
        let res = await fetch(`${API_URL}/invoices/bulk-send-email`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          body,
        });

        if (res.status === 503) {
          await new Promise(r => setTimeout(r, 3000));
          res = await fetch(`${API_URL}/invoices/bulk-send-email`, {
            method: "POST",
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
            body,
          });
        }

        if (!res.ok) throw new Error(await errorDetail(res, "Bulk send failed."));

        totalSent += batch.length;
        successfulBatches += 1;
      } catch (err) {
        failedCount += batch.length;
        // Keep the first real reason — a 429 cooldown message is far more
        // actionable than a bare count of failures.
        if (!failureReason && err?.message) failureReason = err.message;
      }
    }

    bulkSendingRef.current = false;
    setBulkSending(false);
    setSelectedIds(new Set());

    const parts = [
      `${totalSent} invoice${totalSent !== 1 ? "s" : ""} sent in ${successfulBatches} email${successfulBatches !== 1 ? "s" : ""}`,
    ];
    if (skippedCount > 0) parts.push(`${skippedCount} skipped (no email)`);
    if (overLimit) parts.push(`${withEmail.length - maxTotal} beyond ${maxTotal}-invoice limit`);
    if (failedCount > 0) parts.push(`${failedCount} failed`);

    Swal.fire({
      icon: totalSent > 0 ? "success" : "error",
      title: "Bulk Send Complete",
      text: parts.join(" · "),
      footer: failureReason || undefined,
    });
  };

  const sortIcon = (col) => (
    <span className={`ml-1 text-xs ${sort.key === col ? "text-gray-700" : "text-gray-300"}`}>
      {sort.key === col ? (sort.dir === "asc" ? "↑" : "↓") : "↕"}
    </span>
  );

  // ── Actions ──────────────────────────────────────────────────────────────
  const handleMarkPaid = async (invoice) => {
    const { value: method } = await Swal.fire({
      title: `Mark Invoice #${invoice.invoice_number} as Paid`,
      html: `
        <p class="text-sm text-gray-500 mb-4">How was this invoice paid?</p>
        <div class="grid grid-cols-2 gap-2 text-sm" id="method-grid">
          ${PAYMENT_METHODS.map(m => `
            <button type="button" data-value="${m.value}"
              class="method-btn border border-gray-300 rounded-lg px-3 py-2 hover:border-green-500 hover:bg-green-50 text-gray-700 transition">
              ${m.label}
            </button>
          `).join("")}
        </div>
      `,
      showConfirmButton: false,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      didOpen: () => {
        document.querySelectorAll(".method-btn").forEach(btn => {
          btn.addEventListener("click", () => Swal.clickConfirm(), false);
          btn.addEventListener("click", () => {
            document.querySelectorAll(".method-btn").forEach(b => b.classList.remove("border-green-500", "bg-green-50", "font-semibold"));
            btn.classList.add("border-green-500", "bg-green-50", "font-semibold");
            Swal.getConfirmButton(); // keep reference
            // store selection
            document.getElementById("method-grid").dataset.selected = btn.dataset.value;
          });
        });
      },
      preConfirm: () => document.getElementById("method-grid")?.dataset.selected || "other",
    });

    if (!method) return;
    const today = new Date().toISOString().split("T")[0];
    setMarkingPaid(invoice.invoice_id);
    try {
      const res = await fetch(`${API_URL}/invoices/${invoice.invoice_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ paid: true, paid_on: today, payment_method: method }),
      });
      if (!res.ok) throw new Error("Failed to update invoice");
      const updated = await res.json();
      setInvoices(prev => prev.map(inv => inv.invoice_id === updated.invoice_id ? updated : inv));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("error"), text: err.message });
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleMarkUnpaid = async (invoice) => {
    setMarkingPaid(invoice.invoice_id);
    try {
      const res = await fetch(`${API_URL}/invoices/${invoice.invoice_id}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ paid: false, paid_on: "", payment_method: "" }),
      });
      if (!res.ok) throw new Error("Failed to update invoice");
      const updated = await res.json();
      setInvoices(prev => prev.map(inv => inv.invoice_id === updated.invoice_id ? updated : inv));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("error"), text: err.message });
    } finally {
      setMarkingPaid(null);
    }
  };

  const handleSendEmailFromList = async (invoice) => {
    setSendingEmail(invoice.invoice_id);
    try {
      const withLogo = (doc) => ({
        ...doc,
        business_logo_url: activeCompany?.logo_url || "",
        payment_url: (!doc.paid && activeCompany?.stripe_connect_account_id)
          ? `${window.location.origin}/pay/${doc.invoice_id}`
          : null,
      });
      const pdf_base64 = await getInvoicePDFBase64(withLogo(invoice));
      const res = await fetch(`${API_URL}/invoices/${invoice.invoice_id}/send-email`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          recipient_email: invoice.client_email,
          pdf_base64,
          payment_url: (!invoice.paid && activeCompany?.stripe_connect_account_id)
            ? `${window.location.origin}/pay/${invoice.invoice_id}`
            : null,
        }),
      });
      if (!res.ok) throw new Error(await errorDetail(res, "Failed to send email."));
      Swal.fire({ icon: "success", title: "Email Sent", text: `Invoice #${invoice.invoice_number} emailed to ${invoice.client_email}` });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setSendingEmail(null);
    }
  };

  const handleGetPaymentLink = async (invoice) => {
    setGeneratingLink(invoice.invoice_id);
    try {
      const res = await fetch(`${API_URL}/stripe/invoice-checkout/${invoice.invoice_id}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || `Error ${res.status}`);
      }
      const { checkout_url } = await res.json();
      await Swal.fire({
        title: "Payment Link Ready",
        html: `
          <p class="text-sm text-gray-500 mb-3">Share this link with your client for Invoice #${invoice.invoice_number}.</p>
          <input id="payment-link-input" type="text" value="${checkout_url}" readonly
            class="w-full text-xs border border-gray-300 rounded px-3 py-2 bg-gray-50 focus:outline-none" />
          <p class="text-xs text-gray-400 mt-2">Link expires in 24 hours.</p>
        `,
        showCancelButton: true,
        confirmButtonText: "Copy Link",
        cancelButtonText: "Close",
        confirmButtonColor: "#22c55e",
        preConfirm: () => navigator.clipboard.writeText(checkout_url),
      });
    } catch (err) {
      Swal.fire({ icon: "error", title: "Error", text: err.message });
    } finally {
      setGeneratingLink(null);
    }
  };

  const handleDelete = async (invoice) => {
    const result = await Swal.fire({
      icon: "warning",
      title: t("delete_invoice_confirm", { number: invoice.invoice_number }),
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: t("delete"),
      cancelButtonText: t("cancel"),
    });
    if (!result.isConfirmed) return;
    try {
      const res = await fetch(`${API_URL}/invoices/${invoice.invoice_id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed to delete invoice");
      setInvoices(prev => prev.filter(inv => inv.invoice_id !== invoice.invoice_id));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("delete_failed"), text: err.message });
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const pieData = [
    { name: "Paid",    value: stats.paidAmt   },
    { name: "Unpaid",  value: stats.unpaidAmt  },
  ];
  const PIE_COLORS = ["#22c55e", "#fbbf24"];

  const StatusBadge = ({ inv }) => {
    if (inv.paid) {
      const m = METHOD_BADGE[inv.payment_method] || METHOD_BADGE.other;
      return (
        <div className="flex flex-col items-center gap-1">
          <span className="bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-full">
            Paid{inv.paid_on ? ` · ${inv.paid_on}` : ""}
          </span>
          {inv.payment_method && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m.color}`}>{m.label}</span>
          )}
        </div>
      );
    }
    if (isOverdue(inv))
      return <span className="bg-red-100 text-red-700 text-xs font-semibold px-2 py-0.5 rounded-full">Overdue</span>;
    return <span className="bg-yellow-100 text-yellow-700 text-xs font-semibold px-2 py-0.5 rounded-full">Unpaid</span>;
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      {tooltip && (
        <div className="fixed z-[9999] pointer-events-none"
          style={{ left: tooltip.x, top: tooltip.y - 8, transform: "translateX(-50%) translateY(-100%)" }}>
          <div className="bg-gray-800 text-white text-xs rounded-lg px-2.5 py-1.5 shadow-lg w-48 text-center leading-snug">{tooltip.text}</div>
          <div className="w-2 h-2 bg-gray-800 rotate-45 mx-auto -mt-1" />
        </div>
      )}
      <Sidebar handleSignOut={handleSignOut} disableNavigation={bulkSending}>
      <div className="flex flex-col flex-1 p-3 sm:p-6 gap-4">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-800">{t("invoices_title")}</h1>
            {isDemo && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 border border-yellow-200">
                {invoices.length}/{TRIAL_INVOICE_LIMIT} trial
              </span>
            )}
          </div>
          <button
            onClick={() => navigate("/CreateInvoice")}
            disabled={isDemo && invoices.length >= TRIAL_INVOICE_LIMIT}
            title={isDemo && invoices.length >= TRIAL_INVOICE_LIMIT ? "Subscribe to create more invoices" : undefined}
            className={`px-4 py-2 rounded-lg transition text-sm font-medium ${isDemo && invoices.length >= TRIAL_INVOICE_LIMIT ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-green-500 text-white hover:bg-green-600"}`}
          >
            {t("new_invoice_btn")}
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center gap-2 py-20">
            <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
            </svg>
            <span className="text-gray-400 text-sm">Loading invoices…</span>
          </div>
        ) : error ? (
          <p className="text-red-500 bg-white rounded-lg p-4">{error}</p>
        ) : invoices.length === 0 ? (
          <div className="bg-white rounded-xl shadow p-12 text-center">
            <p className="text-gray-400 text-lg mb-4">{t("no_invoices_yet")}</p>
            <button onClick={() => navigate("/CreateInvoice")} className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 text-sm">
              {t("create_first_invoice_btn")}
            </button>
          </div>
        ) : (
          <>
            {/* Stats + Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* KPI cards */}
              <div className="lg:col-span-1 grid grid-cols-2 gap-3">
                <div className="bg-white rounded-xl shadow p-4 border-l-4 border-yellow-400">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Outstanding</p>
                  <p className="text-xl font-bold text-yellow-700">{fmt(stats.unpaidAmt)}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.unpaid.length} invoice{stats.unpaid.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-400">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Collected</p>
                  <p className="text-xl font-bold text-green-700">{fmt(stats.paidAmt)}</p>
                  <p className="text-xs text-gray-400 mt-1">{stats.paid.length} invoice{stats.paid.length !== 1 ? "s" : ""}</p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 border-l-4 border-red-400">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Overdue</p>
                  <p className="text-xl font-bold text-red-600">{stats.overdue.length}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {fmt(stats.overdue.reduce((s, i) => s + (parseFloat(i.total_amount) || 0), 0))} at risk
                  </p>
                </div>
                <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-400">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Total</p>
                  <p className="text-xl font-bold text-blue-700">{invoices.length}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {fmt(stats.paidAmt + stats.unpaidAmt)} lifetime
                  </p>
                </div>
              </div>

              {/* Donut chart */}
              <div className="bg-white rounded-xl shadow p-4 flex flex-col items-center justify-center">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-2">Collected vs Outstanding</p>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={45} outerRadius={68} paddingAngle={3} dataKey="value">
                      {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i]} />)}
                    </Pie>
                    <RechartsTooltip formatter={(v) => fmt(v)} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex gap-4 text-xs text-gray-500 mt-1">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-400 inline-block" />Paid</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-yellow-400 inline-block" />Unpaid</span>
                </div>
              </div>

              {/* Bar chart */}
              <div className="bg-white rounded-xl shadow p-4">
                <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-3">Last 6 Months</p>
                <ResponsiveContainer width="100%" height={150}>
                  <BarChart data={monthlyData} barSize={12} margin={{ top: 0, right: 0, bottom: 0, left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => `$${v >= 1000 ? (v/1000).toFixed(0)+"k" : v}`} />
                    <RechartsTooltip formatter={(v) => fmt(v)} />
                    <Bar dataKey="Paid"   fill="#22c55e" radius={[3,3,0,0]} />
                    <Bar dataKey="Unpaid" fill="#fbbf24" radius={[3,3,0,0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Filter tabs + search */}
            <div className="bg-white rounded-xl shadow">
              {/* Client search bar */}
              <div className="px-4 pt-4 pb-3 border-b border-gray-100" ref={clientSearchRef}>
                <div className="relative max-w-xs">
                  <svg className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search by client…"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setClientDropdownOpen(true);
                      setCurrentPage(1);
                      if (!e.target.value) { setSelectedClient(null); setSelectedIds(new Set()); }
                    }}
                    onFocus={() => setClientDropdownOpen(true)}
                    className="w-full border border-gray-200 rounded-lg pl-8 pr-7 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  />
                  {clientSearch && (
                    <button
                      onClick={() => { setClientSearch(""); setSelectedClient(null); setSelectedIds(new Set()); setClientDropdownOpen(false); setCurrentPage(1); }}
                      className="absolute right-2.5 top-2 text-gray-400 hover:text-gray-600 text-sm leading-none"
                    >✕</button>
                  )}
                  {clientDropdownOpen && clientSuggestions.length > 0 && (
                    <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-lg shadow-lg w-full overflow-hidden">
                      {clientSuggestions.map(client => (
                        <button
                          key={client.client_id}
                          type="button"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            setClientSearch(client.name);
                            setSelectedClient(client);
                            setClientDropdownOpen(false);
                            // Select only invoices visible in the current tab
                            const newBase = invoices.filter(inv => inv.client_name === client.name);
                            const newFiltered =
                              filter === "paid"    ? newBase.filter(i => i.paid) :
                              filter === "unpaid"  ? newBase.filter(i => !i.paid) :
                              filter === "overdue" ? newBase.filter(isOverdue) :
                              newBase;
                            setSelectedIds(new Set(newFiltered.map(inv => inv.invoice_id)));
                          }}
                          className="w-full text-left px-3 py-2 hover:bg-green-50 transition"
                        >
                          <p className="text-sm font-medium text-gray-800">{client.name}</p>
                          <p className="text-xs text-gray-400">{client.email || "No email on file"}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {selectedClient && (
                  <p className="text-xs text-green-600 mt-1.5">
                    Showing {sorted.length} invoice{sorted.length !== 1 ? "s" : ""} for {selectedClient.name}
                  </p>
                )}
              </div>

              {/* Bulk send bar */}
              {selectedIds.size > 0 && (
                <div className={`flex items-center justify-between px-4 py-2 border-b ${
                  hasEmailConflict ? "bg-amber-50 border-amber-100" : "bg-green-50 border-green-100"
                }`}>
                  {hasEmailConflict ? (
                    <span className="text-sm text-amber-700">
                      <span className="font-medium">Multiple clients selected:</span>{" "}
                      {conflictingClients.map(c => `${c.name} (${c.email})`).join(", ")}
                      {" — filter by client to bulk send"}
                    </span>
                  ) : (
                    <span className="text-sm text-green-700 font-medium">
                      {selectedIds.size} invoice{selectedIds.size !== 1 ? "s" : ""} selected
                    </span>
                  )}
                  <button
                    onClick={handleBulkSend}
                    disabled={bulkSending || hasEmailConflict}
                    className={`flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium transition ${
                      hasEmailConflict
                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                        : "bg-green-500 text-white hover:bg-green-600 disabled:opacity-50"
                    }`}
                  >
                    {bulkSending ? (
                      <>
                        <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                        Sending…
                      </>
                    ) : `Send ${selectedIds.size} Invoice${selectedIds.size !== 1 ? "s" : ""}`}
                  </button>
                </div>
              )}

              <div className="flex items-center border-b border-gray-100">
                <div className="flex flex-1">
                  {[
                    { key: "all",     label: "All",     count: tabCounts.all     },
                    { key: "unpaid",  label: "Unpaid",  count: tabCounts.unpaid  },
                    { key: "overdue", label: "Overdue", count: tabCounts.overdue },
                    { key: "paid",    label: "Paid",    count: tabCounts.paid    },
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => { setFilter(tab.key); setCurrentPage(1); }}
                      className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition -mb-px ${
                        filter === tab.key
                          ? "border-green-500 text-green-600"
                          : "border-transparent text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {tab.label}
                      <span className={`text-xs rounded-full px-1.5 py-0.5 font-semibold ${
                        filter === tab.key ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-500"
                      }`}>
                        {tab.count}
                      </span>
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5 px-4 text-xs text-gray-500 whitespace-nowrap">
                  <span>Per page</span>
                  <select
                    value={pageSize}
                    onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                    className="border border-gray-200 rounded px-1.5 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-green-400"
                  >
                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>

              {sorted.length === 0 ? (
                <div className="py-12 text-center text-gray-400 text-sm">No invoices in this category.</div>
              ) : (
                <>
                  {/* Desktop table */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <thead>
                        <tr className="bg-gray-100 text-gray-600 text-xs uppercase tracking-wide">
                          <th className="px-4 py-3 w-8">
                            <input
                              type="checkbox"
                              checked={allPageSelected}
                              ref={el => { if (el) el.indeterminate = somePageSelected && !allPageSelected; }}
                              onChange={toggleSelectAll}
                              className="rounded border-gray-300 cursor-pointer"
                            />
                          </th>
                          <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort("invoice_number")}># {sortIcon("invoice_number")}</th>
                          <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort("client_name")}>Client {sortIcon("client_name")}</th>
                          <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort("date")}>Date {sortIcon("date")}</th>
                          <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort("due_date")}>Due {sortIcon("due_date")}</th>
                          <th className="px-4 py-3 text-right cursor-pointer select-none" onClick={() => toggleSort("total_amount")}>Amount {sortIcon("total_amount")}</th>
                          <th className="px-4 py-3 text-center cursor-pointer select-none" onClick={() => toggleSort("paid")}>Status {sortIcon("paid")}</th>
                          <th className="px-4 py-3 text-center">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {paginated.map((inv) => (
                          <tr key={inv.invoice_id}
                            onClick={() => navigate(`/UpdateInvoice/${inv.invoice_id}`, { state: { invoice: inv, viewOnly: true } })}
                            className={`cursor-pointer hover:bg-gray-50 transition ${isOverdue(inv) ? "bg-red-50/40" : ""} ${selectedIds.has(inv.invoice_id) ? "bg-green-50/60" : ""}`}>
                            <td className="px-4 py-3 w-8" onClick={e => e.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={selectedIds.has(inv.invoice_id)}
                                onChange={() => toggleSelect(inv.invoice_id)}
                                className="rounded border-gray-300 cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3 font-semibold text-gray-700">#{inv.invoice_number}</td>
                            <td className="px-4 py-3 text-gray-800">{inv.client_name}</td>
                            <td className="px-4 py-3 text-gray-500 whitespace-nowrap">{inv.date}</td>
                            <td className={`px-4 py-3 whitespace-nowrap ${isOverdue(inv) ? "text-red-500 font-medium" : "text-gray-500"}`}>
                              {inv.due_date || "—"}
                            </td>
                            <td className="px-4 py-3 text-right font-semibold text-gray-800">{fmt(inv.total_amount)}</td>
                            <td className="px-4 py-3 text-center"><StatusBadge inv={inv} /></td>
                            <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                              <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                {inv.paid ? (
                                  <>
                                    <button onClick={() => handleMarkUnpaid(inv)} disabled={markingPaid === inv.invoice_id}
                                      className="text-xs px-2.5 py-1 rounded-md bg-orange-50 text-orange-600 hover:bg-orange-100 font-medium disabled:opacity-50">
                                      {markingPaid === inv.invoice_id ? "…" : "Unmark Paid"}
                                    </button>
                                    {inv.client_email ? (
                                      <button onClick={() => handleSendEmailFromList(inv)} disabled={sendingEmail === inv.invoice_id}
                                        className="text-xs px-2.5 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium disabled:opacity-50">
                                        {sendingEmail === inv.invoice_id ? "…" : "Email"}
                                      </button>
                                    ) : (
                                      <button
                                        onMouseEnter={(e) => showTooltip(e, "Add a client email to this invoice to enable sending")}
                                        onMouseLeave={hideTooltip}
                                        className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-400 font-medium cursor-not-allowed">Email</button>
                                    )}
                                    <button
                                      onMouseEnter={(e) => showTooltip(e, "Mark the invoice as unpaid before deleting")}
                                      onMouseLeave={hideTooltip}
                                      className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-400 font-medium cursor-not-allowed">{t("delete")}</button>
                                  </>
                                ) : (
                                  <>
                                    <button onClick={() => navigate(`/UpdateInvoice/${inv.invoice_id}`, { state: { invoice: inv, viewOnly: false } })}
                                      className="text-xs px-2.5 py-1 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 font-medium">Edit</button>
                                    <button onClick={() => handleMarkPaid(inv)} disabled={markingPaid === inv.invoice_id}
                                      className="text-xs px-2.5 py-1 rounded-md bg-green-50 text-green-600 hover:bg-green-100 font-medium disabled:opacity-50">
                                      {markingPaid === inv.invoice_id ? "…" : "Mark Paid"}
                                    </button>
                                    {isAdmin && (activeCompany?.stripe_connect_account_id ? (
                                      <button onClick={() => handleGetPaymentLink(inv)} disabled={generatingLink === inv.invoice_id}
                                        className="text-xs px-2.5 py-1 rounded-md bg-purple-50 text-purple-600 hover:bg-purple-100 font-medium disabled:opacity-50">
                                        {generatingLink === inv.invoice_id ? "…" : "Pay Link"}
                                      </button>
                                    ) : (
                                      <button
                                        onMouseEnter={(e) => showTooltip(e, "Connect Stripe in Company Settings to enable payment links")}
                                        onMouseLeave={hideTooltip}
                                        className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-400 font-medium cursor-not-allowed">Pay Link</button>
                                    ))}
                                    {inv.client_email ? (
                                      <button onClick={() => handleSendEmailFromList(inv)} disabled={sendingEmail === inv.invoice_id}
                                        className="text-xs px-2.5 py-1 rounded-md bg-blue-50 text-blue-600 hover:bg-blue-100 font-medium disabled:opacity-50">
                                        {sendingEmail === inv.invoice_id ? "…" : "Email"}
                                      </button>
                                    ) : (
                                      <button
                                        onMouseEnter={(e) => showTooltip(e, "Add a client email to this invoice to enable sending")}
                                        onMouseLeave={hideTooltip}
                                        className="text-xs px-2.5 py-1 rounded-md bg-gray-100 text-gray-400 font-medium cursor-not-allowed">Email</button>
                                    )}
                                    <button onClick={() => handleDelete(inv)}
                                      className="text-xs px-2.5 py-1 rounded-md bg-red-50 text-red-500 hover:bg-red-100 font-medium">{t("delete")}</button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile cards */}
                  <div className="md:hidden divide-y divide-gray-100">
                    {sorted.map((inv) => (
                      <div key={inv.invoice_id}
                        onClick={() => navigate(`/UpdateInvoice/${inv.invoice_id}`, { state: { invoice: inv, viewOnly: true } })}
                        className={`p-4 cursor-pointer ${isOverdue(inv) ? "bg-red-50/40" : ""} ${selectedIds.has(inv.invoice_id) ? "bg-green-50/60" : ""}`}>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-2">
                            <div onClick={e => e.stopPropagation()} className="mt-0.5">
                              <input
                                type="checkbox"
                                checked={selectedIds.has(inv.invoice_id)}
                                onChange={() => toggleSelect(inv.invoice_id)}
                                className="rounded border-gray-300 cursor-pointer"
                              />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-800">#{inv.invoice_number} · {inv.client_name}</p>
                              <p className="text-xs text-gray-400 mt-0.5">
                                {inv.date}{inv.due_date ? ` · Due ${inv.due_date}` : ""}
                                {isOverdue(inv) && <span className="ml-1 text-red-500 font-medium">Overdue</span>}
                              </p>
                            </div>
                          </div>
                          <div className="text-right ml-3 flex-shrink-0">
                            <p className="text-base font-bold text-gray-800">{fmt(inv.total_amount)}</p>
                            <StatusBadge inv={inv} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2" onClick={(e) => e.stopPropagation()}>
                          {inv.paid ? (
                            <>
                              <button onClick={() => handleMarkUnpaid(inv)} disabled={markingPaid === inv.invoice_id}
                                className="flex-1 text-xs py-1.5 rounded-lg bg-orange-50 text-orange-600 font-medium hover:bg-orange-100 disabled:opacity-50">
                                {markingPaid === inv.invoice_id ? "…" : "Unmark Paid"}
                              </button>
                              {inv.client_email && (
                                <button onClick={() => handleSendEmailFromList(inv)} disabled={sendingEmail === inv.invoice_id}
                                  className="flex-1 text-xs py-1.5 rounded-lg bg-blue-50 text-blue-600 font-medium hover:bg-blue-100 disabled:opacity-50">
                                  {sendingEmail === inv.invoice_id ? "…" : "Email"}
                                </button>
                              )}
                            </>
                          ) : (
                            <>
                              <button onClick={() => navigate(`/UpdateInvoice/${inv.invoice_id}`, { state: { invoice: inv, viewOnly: false } })}
                                className="flex-1 text-xs py-1.5 rounded-lg bg-gray-200 text-gray-700 font-medium hover:bg-gray-300">Edit</button>
                              <button onClick={() => handleMarkPaid(inv)} disabled={markingPaid === inv.invoice_id}
                                className="flex-1 text-xs py-1.5 rounded-lg bg-green-50 text-green-600 font-medium hover:bg-green-100 disabled:opacity-50">
                                {markingPaid === inv.invoice_id ? "…" : "Mark Paid"}
                              </button>
                              {isAdmin && (activeCompany?.stripe_connect_account_id ? (
                                <button onClick={() => handleGetPaymentLink(inv)} disabled={generatingLink === inv.invoice_id}
                                  className="flex-1 text-xs py-1.5 rounded-lg bg-purple-50 text-purple-600 font-medium hover:bg-purple-100 disabled:opacity-50">
                                  {generatingLink === inv.invoice_id ? "…" : "Pay Link"}
                                </button>
                              ) : (
                                <button disabled className="flex-1 text-xs py-1.5 rounded-lg bg-gray-100 text-gray-400 font-medium cursor-not-allowed">Pay Link</button>
                              ))}
                              {inv.client_email ? (
                                <button onClick={() => handleSendEmailFromList(inv)} disabled={sendingEmail === inv.invoice_id}
                                  className="flex-1 text-xs py-1.5 rounded-lg bg-blue-50 text-blue-600 font-medium hover:bg-blue-100 disabled:opacity-50">
                                  {sendingEmail === inv.invoice_id ? "…" : "Email"}
                                </button>
                              ) : (
                                <button disabled className="flex-1 text-xs py-1.5 rounded-lg bg-gray-100 text-gray-400 font-medium cursor-not-allowed">Email</button>
                              )}
                              <button onClick={() => handleDelete(inv)}
                                className="flex-1 text-xs py-1.5 rounded-lg bg-red-50 text-red-500 font-medium hover:bg-red-100">{t("delete")}</button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination controls */}
                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-t border-gray-100 gap-2 text-sm text-gray-600">
                      <span>
                        Showing {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, sorted.length)} of {sorted.length}
                      </span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => setCurrentPage(1)} disabled={safePage === 1}
                          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100" title="First">«</button>
                        <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
                          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100" title="Previous">‹</button>
                        {pageNumbers().map((p, i) =>
                          p === "…" ? (
                            <span key={`e${i}`} className="px-2 py-1">…</span>
                          ) : (
                            <button key={p} onClick={() => setCurrentPage(p)}
                              className={`px-3 py-1 rounded border ${p === safePage ? "bg-green-500 text-white border-green-500" : "border-gray-200 hover:bg-gray-100"}`}>
                              {p}
                            </button>
                          )
                        )}
                        <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
                          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100" title="Next">›</button>
                        <button onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages}
                          className="px-2 py-1 rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-100" title="Last">»</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}

        <Link to="/" className="self-start">
          <button className="bg-gray-500 text-white py-2 px-6 rounded-lg hover:bg-gray-600 transition text-sm">
            {t("back_to_dashboard_btn")}
          </button>
        </Link>
      </div>
      </Sidebar>
    </div>
  );
};

export default InvoiceList;
